import * as duckdb from '@duckdb/duckdb-wasm';

export class DuckDBService {
  constructor() {
    this.isInitialized = false;
    this.isDataLoaded = false;
    this.db = null;
    this.rawConn = null;
    this.logger = new duckdb.ConsoleLogger();
    this.config = null;

    this.emptyResult = {
      toArray: () => [],
      toString: () => "",
      getChild: () => null,
      schema: [],
      numCols: 0,
      numRows: 0,
    };
    
    this.conn = {
      query: async (queryString) => {
        try {
          if (!this.isInitialized || !this.isDataLoaded || !this.rawConn) {
            return this.emptyResult;
          }
          return await this.rawConn.query(queryString);
        } catch (error) {
          console.error(`Query failed: ${error.message}`);
          return this.emptyResult;
        }
      },
      // Raw query that bypasses isDataLoaded check and doesn't poison state on error
      queryRaw: async (queryString) => {
        if (!this.isInitialized || !this.rawConn) {
          return this.emptyResult;
        }
        return await this.rawConn.query(queryString);
      },
      loadData: async (queryString) => {
        if (!this.isInitialized) {
          console.error('Cannot load data: DuckDB not initialized');
          return false;
        }
    
        try {
          await this.rawConn.query(queryString);
          return true;
        } catch (error) {
          console.error('Error loading data:', error);
          this.isDataLoaded = false;
          return false;
        }
      }
    };
  }

  async initialize({ proxyUrl, apiUrl, scopedToken }) {
    try {
      // Store config for later use
      this.config = { proxyUrl, apiUrl, scopedToken };

      // CDN bundles are derived from the imported bindings, so the wasm/worker
      // version can never drift from the JS version in package.json (and the
      // index.html importmap). Pin the package exactly - a mismatched worker
      // fails in subtle ways rather than loudly.
      const bundles = duckdb.getJsDelivrBundles();
      const selected = await duckdb.selectBundle(bundles);

      // Fetch worker script and create blob URL to avoid CORS issues
      const workerResponse = await fetch(selected.mainWorker);
      const workerCode = await workerResponse.text();
      const workerBlob = new Blob([workerCode], { type: 'application/javascript' });
      const workerBlobUrl = URL.createObjectURL(workerBlob);

      const bundle = { ...selected, mainWorker: workerBlobUrl };
      const worker = new Worker(bundle.mainWorker);

      //const duckDBConfig = {
      //  maximumThreads: 4, // Adjust the number based on your needs
      //};

      this.db = new duckdb.AsyncDuckDB(this.logger, worker/*, duckDBConfig*/);

      console.log('Instantiating DuckDB...');
      await this.db.instantiate(bundle.mainModule);
      console.log('DuckDB instantiated successfully');

      console.log('Creating DuckDB connection...');
      this.rawConn = await this.db.connect();
      console.log('DuckDB connection created successfully');

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Error initializing DuckDB:', error);
      this.isInitialized = false;
      return false;
    }
  }

  async downloadAndInsertParquet(path, tableName, progressCallback = null, options = {}) {
    const { throwOnError = false } = options;
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }

    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);

      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/vnd.apache.parquet'
        }
      });
      if (!response.ok) {
        const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        httpError.status = response.status;
        throw httpError;
      }
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}.parquet`, new Uint8Array(arrayBuffer));
      const tableExistsResult = await this.rawConn.query(
        `SELECT COUNT(*) as cnt FROM information_schema.tables WHERE table_name = '${tableName}'`
      );
      const tableExists = tableExistsResult.toArray()[0].cnt > 0;
      if (tableExists) {
        await this.conn.loadData(`INSERT INTO ${tableName} BY NAME SELECT * FROM parquet_scan('${tableName}.parquet')`);
      } else {
        await this.conn.loadData(`CREATE TABLE ${tableName} AS SELECT * FROM parquet_scan('${tableName}.parquet')`);
      }
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} populated successfully in ${totalTime}ms`);
      
      // Report completion
      if (progressCallback) {
        progressCallback({
          tableName,
          status: 'completed',
          timeMs: totalTime
        });
      }
      
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }

  async downloadAndInsertParquetWithPaging(path, tableName, progressCallback = null, options = {}) {
    const { throwOnError = false } = options;
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const baseUrl = new URL(`https://${proxyUrl}${path}`);
      baseUrl.searchParams.append('url', apiUrl);
      baseUrl.searchParams.append('scopedToken', scopedToken);
      
      const startTime = performance.now();
      this.logger.log(`Starting paged fetch for ${tableName} at ${startTime}`);
      
      // First create the table structure
      let pageNum = 1;
      const firstPageUrl = new URL(baseUrl.toString());
      firstPageUrl.searchParams.append('page', pageNum);
      
      this.logger.log(`Fetching first page from ${firstPageUrl}`);
      const firstResponse = await fetch(firstPageUrl, { 
        method: 'GET',
        headers: {
            'Accept': 'application/vnd.apache.parquet'

        }
      });
      
      if (!firstResponse.ok) {
        const httpError = new Error(`HTTP ${firstResponse.status}: ${firstResponse.statusText}`);
        httpError.status = firstResponse.status;
        throw httpError;
      }
      
      const firstBlob = await firstResponse.blob();
      if (firstBlob.size === 0) {
        this.logger.log(`No data received for ${tableName}`);
        return false;
      }
      
      const firstArrayBuffer = await firstBlob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}_page_${pageNum}.parquet`, new Uint8Array(firstArrayBuffer));
      
      // Create the table with the first page
      await this.conn.loadData(`CREATE TABLE IF NOT EXISTS ${tableName} AS SELECT * FROM parquet_scan('${tableName}_page_${pageNum}.parquet')`);
      this.logger.log(`Table ${tableName} created with page ${pageNum}`);
      
      // Continue loading additional pages.
      // Endpoints signal "no more data" differently: some (e.g. tag-pivot-as-parquet)
      // return an empty body (blob.size === 0), while others (e.g. status-history-paging)
      // return a valid but row-less parquet past the last page — its blob still carries the
      // PAR1 magic + footer (a few hundred bytes), so blob.size is never 0. Handle both by
      // also stopping when a fetched page contains zero rows.
      let totalRowsInserted = await this.getRowCount(tableName);
      // If the first page already came back empty, there is nothing more to fetch.
      let hasMoreData = totalRowsInserted > 0;
      
      while (hasMoreData) {
        pageNum++;
        const pageUrl = new URL(baseUrl.toString());
        pageUrl.searchParams.append('page', pageNum);
        
        this.logger.log(`Fetching page ${pageNum} from ${pageUrl}`);
        const response = await fetch(pageUrl, { 
          method: 'GET',
          headers: {
              'Accept': 'application/vnd.apache.parquet'

          }
        });
        
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        
        const blob = await response.blob();
        if (blob.size === 0) {
          this.logger.log(`No more data available after page ${pageNum-1} (empty response)`);
          hasMoreData = false;
          break;
        }

        const arrayBuffer = await blob.arrayBuffer();
        const pageFile = `${tableName}_page_${pageNum}.parquet`;
        await this.db.registerFileBuffer(pageFile, new Uint8Array(arrayBuffer));

        // A non-empty body may still be a row-less parquet (end of data); check before inserting.
        const pageRowCount = await this.getParquetRowCount(pageFile);
        if (pageRowCount === 0) {
          this.logger.log(`No more data available after page ${pageNum-1} (empty parquet)`);
          hasMoreData = false;
          break;
        }

        // Append data to the existing table
        await this.conn.loadData(`INSERT INTO ${tableName} SELECT * FROM parquet_scan('${pageFile}')`);
        totalRowsInserted += pageRowCount;

        this.logger.log(`Inserted ${pageRowCount} rows from page ${pageNum}`);
        
        // Report progress if callback provided
        if (progressCallback) {
          progressCallback({
            tableName,
            currentPage: pageNum,
            rowsLoaded: totalRowsInserted,
            status: 'loading'
          });
        }
      }
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully with ${totalRowsInserted} total rows in ${totalTime}ms`);
      
      // Report completion
      if (progressCallback) {
        progressCallback({
          tableName,
          totalPages: pageNum,
          rowsLoaded: totalRowsInserted,
          status: 'completed',
          timeMs: totalTime
        });
      }
      
      return true;
    } catch (error) {
      this.logger.log(`Error creating paged table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }
  
  // Helper method to get row count
  async getRowCount(tableName) {
    const result = await this.rawConn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
    const rows = result.toArray();
    return rows.length > 0 ? Number(rows[0].count) : 0;
  }

  // Row count of a registered parquet buffer (read from its footer metadata) without
  // inserting it. Uses rawConn directly because conn.query is gated by isDataLoaded,
  // which is still false while data is being loaded.
  async getParquetRowCount(fileName) {
    const result = await this.rawConn.query(`SELECT COUNT(*) as count FROM parquet_scan('${fileName}')`);
    const rows = result.toArray();
    return rows.length > 0 ? Number(rows[0].count) : 0;
  }

  async downloadAndInsertArrow(path, tableName, progressCallback = null) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download Arrow file: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);
  
      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
  
      // Stream the response if the browser supports it
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      
      // Define EOS signal according to the Arrow IPC streaming format
      const EOS = new Uint8Array([255, 255, 255, 255, 0, 0, 0, 0]);
      
      if (response.body && response.body.getReader) {
        // Stream processing for better memory efficiency
        const reader = response.body.getReader();
        const inserts = [];
        
        let done = false;
        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (!done) {
            inserts.push(this.rawConn.insertArrowFromIPCStream(result.value, { name: tableName }));
          }
        }
        
        // Write the End-of-Stream marker
        inserts.push(this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName }));
        await Promise.all(inserts);
      } else {
        // Fallback for browsers that don't support streaming
        const arrayBuffer = await response.arrayBuffer();
        await this.rawConn.insertArrowFromIPCStream(new Uint8Array(arrayBuffer), { name: tableName });
        await this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName });
      }
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully in ${totalTime}ms`);
      
      // Report completion
      if (progressCallback) {
        progressCallback({
          tableName,
          status: 'completed',
          timeMs: totalTime
        });
      }
      
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      return false;
    }
  }

async downloadAndInsertJSON(path, tableName, progressCallback = null) {
    if (!this.isInitialized || !this.config) {
      console.warn('Cannot download JSON: DuckDB not initialized or config missing', { isInitialized: this.isInitialized, config: this.config });
      return false;
    }

    try {
      const { proxyUrl, apiUrl, scopedToken } = this.config;
      const url = new URL(`https://${proxyUrl}${path}`);
      url.searchParams.append('url', apiUrl);
      url.searchParams.append('scopedToken', scopedToken);

      console.log(`[DuckDB] Constructed URL for ${tableName}: ${url.toString()}`); // Debug: Log full URL

      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${url} at ${startTime}`);
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Accept': 'application/json' // Ensure JSON response
          // Add 'Authorization' if your API expects it (uncomment if needed)
          // 'Authorization': `Bearer ${scopedToken}`
        }
      });

      console.log(`[DuckDB] Response status: ${response.status}, Content-Type: ${response.headers.get('Content-Type')}`); // Debug: Log response details

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      
      const jsonData = await response.json();
      console.log(`[DuckDB] Parsed JSON data for ${tableName}:`, jsonData); // Debug: Log raw JSON

      const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      if (dataArray.length === 0) {
        throw new Error('No data received from API');
      }

      const jsonString = JSON.stringify(dataArray);
      const encoder = new TextEncoder();
      const jsonBuffer = encoder.encode(jsonString);
      
      await this.db.registerFileBuffer(`${tableName}.json`, jsonBuffer);
      console.log(`[DuckDB] JSON buffer registered for ${tableName}`); // Debug: Confirm buffer registration
      
      // Create table if not exists using auto-inference, then insert data
      // First, create empty table with inferred schema if it doesn't exist
      await this.conn.loadData(`
        CREATE TABLE IF NOT EXISTS ${tableName} AS 
        SELECT * FROM read_json_auto('${tableName}.json') WHERE false;
      `);
      
      // Then insert the data
      await this.conn.loadData(`
        INSERT INTO ${tableName} 
        SELECT * FROM read_json_auto('${tableName}.json');
      `);
      
      // Verify insertion with row count
      const rowCountResult = await this.rawConn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
      const rowCount = rowCountResult.toArray()[0].count;
      console.log(`[DuckDB] Inserted ${rowCount} rows into ${tableName}`); // Debug: Confirm rows inserted
      
      if (rowCount === 0) {
        throw new Error('Insertion succeeded but no rows were added to the table');
      }
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} populated successfully in ${totalTime}ms`);
      
      // Report completion
      if (progressCallback) {
        progressCallback({
          tableName,
          status: 'completed',
          timeMs: totalTime
        });
      }
      
      return true;
    } catch (error) {
      this.logger.log(`Error populating table ${tableName} from ${path}: ${error.message}`);
      throw error; // Propagate to caller
    }
  }

  async insertJSONToDuckDB(data, tableName) {
    if (!this.isInitialized) {
      console.warn('Cannot insert JSON: DuckDB not initialized');
      return false;
    }

    if (!data || (Array.isArray(data) && data.length === 0)) {
      console.warn(`No data provided for table ${tableName}`);
      return false;
    }

    try {
      const startTime = performance.now();
      
      // Ensure data is an array
      const dataArray = Array.isArray(data) ? data : [data];
      
      // Convert data to JSON string and then to buffer
      const jsonString = JSON.stringify(dataArray);
      const encoder = new TextEncoder();
      const jsonBuffer = encoder.encode(jsonString);
      
      // Register the buffer with DuckDB
      await this.db.registerFileBuffer(`${tableName}.json`, jsonBuffer);

      // Create table from JSON. Replace rather than reuse: a stale table from
      // a previous load would silently keep its old schema.
      await this.rawConn.query(`DROP TABLE IF EXISTS ${tableName}`);
      await this.rawConn.query(`
        CREATE TABLE ${tableName} AS
        SELECT * FROM read_json_auto('${tableName}.json')
      `);

      this.logger.log(`Table ${tableName} created successfully with ${dataArray.length} rows in ${performance.now() - startTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from data: ${error.message}`);
      return false;
    }
  }

  // Append rows to a table created by insertJSONToDuckDB. Used to load large
  // demo tables in chunks - one multi-megabyte JSON blob is slow to serialise
  // and to parse.
  async appendJSONToDuckDB(data, tableName) {
    if (!this.isInitialized) {
      console.warn('Cannot append JSON: DuckDB not initialized');
      return false;
    }

    const dataArray = Array.isArray(data) ? data : [data];
    if (dataArray.length === 0) return true;

    try {
      const fileName = `${tableName}_append.json`;
      const jsonBuffer = new TextEncoder().encode(JSON.stringify(dataArray));

      // registerFileBuffer replaces an existing registration, so the same
      // name can be reused for every chunk.
      await this.db.registerFileBuffer(fileName, jsonBuffer);
      await this.rawConn.query(`
        INSERT INTO ${tableName} BY NAME
        SELECT * FROM read_json_auto('${fileName}')
      `);
      return true;
    } catch (error) {
      this.logger.log(`Error appending rows to ${tableName}: ${error.message}`);
      return false;
    }
  }

  async logQuery(query) {
    try {
      const result = await this.conn.query(query);
      
      const data = [];
      for (let row of result) {
        data.push({...row});
      }

      console.table(data);
      return data;
    } catch (error) {
      this.logger.log(`Error executing query "${query}": ${error.message}`);
      return [];
    }
  }

  async showSchema(tableName) {
    try {
      const result = await this.conn.query(`
        SELECT 
          column_name,
          data_type,
          is_nullable
        FROM information_schema.columns 
        WHERE table_name = '${tableName}'
      `);
      
      const data = result.toArray();
      
      console.log("Data to display:", data); // Check the actual data
      console.table(data);
      
      return data;
    } catch (error) {
      console.error(`Error getting schema for table ${tableName}:`, error);
      return [];
    }
  }
}